import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  ImpactRef,
  PriorityRef,
  TicketFormOptions,
  TicketStatusRef,
  TicketSubcategoryRef,
  TicketTypeRef,
  UrgencyRef,
} from '@central/contracts';
import { chavesDeApoio, obterOpcoesDeFormulario } from '@/features/tickets/lookups.api';
import { ROTULO_DE_NIVEL, ROTULO_DE_TIPO, type Nivel, type TipoDeChamado } from '@/features/tickets/types';

/**
 * Organizações, equipes, situações e classificação do tenant.
 *
 * Uma consulta só para todas as listas, e a mesma resposta serve de opção de
 * seletor e de dicionário do detalhe. Ficam "frescas" por bastante tempo
 * porque mudam em ritmo de configuração, não de operação — revalidar a cada
 * montagem de tela seria uma requisição por navegação para um dado que passa o
 * dia inteiro igual.
 */
const MEIA_HORA = 30 * 60_000;

export function useOpcoesDeFormulario(): UseQueryResult<TicketFormOptions> {
  return useQuery({
    queryKey: chavesDeApoio.formulario,
    queryFn: obterOpcoesDeFormulario,
    staleTime: MEIA_HORA,
    gcTime: MEIA_HORA,
  });
}

/**
 * O filtro de `active` é da tela, não da API.
 *
 * A lista vem inteira de propósito: a organização desativada precisa continuar
 * resolvendo o nome dos chamados antigos. Quem **oferece** uma escolha nova é
 * que descarta o que saiu de operação — e o registro que já aponta para ela
 * continua tendo nome.
 */
export function apenasAtivos<T extends { active: boolean }>(itens: readonly T[]): T[] {
  return itens.filter((item) => item.active);
}

/**
 * Nome por trás de um identificador, ou `null` se as opções ainda não
 * chegaram — ou se o id não está entre elas.
 *
 * `null` e "não existe" são a mesma resposta aqui de propósito: quem exibe
 * decide o que dizer, e nenhuma das duas autoriza inventar um rótulo.
 */
function nomeEm(
  itens: ReadonlyArray<{ id: string; name: string }> | undefined,
  id: string | null | undefined,
): string | null {
  if (!id || !itens) return null;
  return itens.find((item) => item.id === id)?.name ?? null;
}

export const nomeDaOrganizacao = (
  opcoes: TicketFormOptions | undefined,
  id: string | null | undefined,
): string | null => nomeEm(opcoes?.organizations, id);

export const nomeDaEquipe = (
  opcoes: TicketFormOptions | undefined,
  id: string | null | undefined,
): string | null => nomeEm(opcoes?.teams, id);

export function situacaoPorId(
  opcoes: TicketFormOptions | undefined,
  id: string | null | undefined,
): TicketStatusRef | null {
  if (!id || !opcoes) return null;
  return opcoes.statuses.find((situacao) => situacao.id === id) ?? null;
}

export const nomeDaCategoria = (
  opcoes: TicketFormOptions | undefined,
  id: string | null | undefined,
): string | null => nomeEm(opcoes?.categories, id);

export const nomeDaSubcategoria = (
  opcoes: TicketFormOptions | undefined,
  id: string | null | undefined,
): string | null => nomeEm(opcoes?.subcategories, id);

/**
 * As subcategorias de uma categoria.
 *
 * O recorte é da tela porque a API devolve a lista **plana**, com `categoryId`
 * no corpo: é a mesma forma que a coluna do chamado tem, e a mesma que o envio
 * usa. Agrupar do lado do servidor criaria uma segunda representação do
 * vínculo, e a tela de detalhe — que tem só o id da subcategoria — teria de
 * varrer as categorias para achar o nome.
 *
 * Sem categoria escolhida a resposta é lista vazia, e não "todas": oferecer as
 * 80 subcategorias soltas é oferecer o par incoerente que o banco recusa.
 */
export function subcategoriasDaCategoria(
  opcoes: TicketFormOptions | undefined,
  categoriaId: string | null | undefined,
): TicketSubcategoryRef[] {
  if (!categoriaId || !opcoes) return [];
  return opcoes.subcategories.filter((sub) => sub.categoryId === categoriaId);
}

// ── helpers de campo configurável ─────────────────────────────────────────

/** Rótulo de prioridade/urgência/impacto com fallback para o valor estático. */
export function rotuloDaPrioridade(
  opcoes: TicketFormOptions | undefined,
  level: Nivel,
): string {
  return opcoes?.priorities.find((p) => p.level === level)?.label ?? ROTULO_DE_NIVEL[level];
}

export function rotuloDaUrgencia(
  opcoes: TicketFormOptions | undefined,
  level: Nivel,
): string {
  return opcoes?.urgencies.find((u) => u.level === level)?.label ?? ROTULO_DE_NIVEL[level];
}

export function rotuloDaImpacto(
  opcoes: TicketFormOptions | undefined,
  level: Nivel,
): string {
  return opcoes?.impacts.find((i) => i.level === level)?.label ?? ROTULO_DE_NIVEL[level];
}

export function rotuloDeTipo(
  opcoes: TicketFormOptions | undefined,
  type: TipoDeChamado,
): string {
  return opcoes?.ticketTypes.find((t) => t.type === type)?.label ?? ROTULO_DE_TIPO[type];
}

/** Lista de prioridades ativas, ordenadas, com fallback para a lista estática. */
export function listaDePrioridades(opcoes: TicketFormOptions | undefined): PriorityRef[] {
  if (!opcoes?.priorities.length) {
    return (['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const).map((level, i) => ({
      level,
      label: ROTULO_DE_NIVEL[level],
      color: null,
      order: i + 1,
      active: true,
    }));
  }
  return opcoes.priorities;
}

export function listaDeUrgencias(opcoes: TicketFormOptions | undefined): UrgencyRef[] {
  if (!opcoes?.urgencies.length) {
    return (['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const).map((level, i) => ({
      level,
      label: ROTULO_DE_NIVEL[level],
      color: null,
      order: i + 1,
      active: true,
    }));
  }
  return opcoes.urgencies;
}

export function listaDeImpactos(opcoes: TicketFormOptions | undefined): ImpactRef[] {
  if (!opcoes?.impacts.length) {
    return (['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const).map((level, i) => ({
      level,
      label: ROTULO_DE_NIVEL[level],
      color: null,
      order: i + 1,
      active: true,
    }));
  }
  return opcoes.impacts;
}

export function listaDeTipos(opcoes: TicketFormOptions | undefined): TicketTypeRef[] {
  if (!opcoes?.ticketTypes.length) {
    return (['INCIDENT', 'REQUEST', 'PROBLEM', 'CHANGE', 'TASK'] as const).map((type, i) => ({
      type,
      label: ROTULO_DE_TIPO[type],
      order: i + 1,
      active: true,
    }));
  }
  return opcoes.ticketTypes;
}
